import { FC, useEffect, useState } from 'react';
import classNamesBind from 'classnames/bind';
import SearchNav from 'src/common/components/Search/SearchNav';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import Link from 'next/link';
import Logo from 'src/common/components/Logo';
import style from './Header.module.scss';
import Navigation from '../Navigation';

const cx = classNamesBind.bind(style);
export type LogoSizes = 'full' | 'joy-only';

export type HeaderProps = {
  /**
   * Allows for custom content to be rendered in the Header
   */
  children?: React.ReactNode;
  /**
   * Should the Header be fixed with `position: fixed`?
   * Defaults to `true`
   */
  fixed?: boolean;
  /**
   * Should the Header include the search bar?
   * Defaults to `true`
   */
  withLogo?: boolean;
  /**
   * Should the Header render the GMB logo?
   * Defaults to `false`
   * useStickySearch pages default their withSearch prop to false, and conditionally mutate to true
   */
  withSearch?: boolean;
  /**
   * Should the Header include the navigation items?
   * Defaults to `true`
   */
  withNavigation?: boolean;
  /**
   * Determines the Header's color theme
   * Defaults to `pearl`
   */
  variant?: 'ivory' | 'pearl';
  /**
   * Determines if a bottom border should appear on scroll down
   * Defaults to `false`
   */
  showBottomBorderOnScroll?: boolean;
  /**
   * Determines if the Header should be hidden from a specific breakpoint
   * Defaults to `false` for all breakpoints
   */
  hideAtBreakpoint?: {
    xs?: boolean;
    s?: boolean;
    m?: boolean;
  };
};

const Header: FC<HeaderProps> = ({
  children,
  fixed = true,
  withLogo = true,
  withSearch = false,
  withNavigation = true,
  variant = 'pearl',
  showBottomBorderOnScroll = false,
  hideAtBreakpoint = { xs: false, s: false, m: false },
}) => {
  const [scrollIsAtTop, setScrollAtTop] = useState<boolean>(true);
  const { isMobileNativeWebView } = useMobileNativeWebView();

  useEffect(() => {
    if (showBottomBorderOnScroll) {
      const handleScrollTop = () => {
        if (window.scrollY === 0) setScrollAtTop(true);
        else setScrollAtTop(false);
      };
      window.addEventListener('scroll', handleScrollTop);
      return () => window.removeEventListener('scroll', handleScrollTop);
    }
    return undefined;
  }, [showBottomBorderOnScroll]);

  // If user is in mobile app web view, don't render the header
  if (isMobileNativeWebView) return null;

  return (
    <header
      className={cx(variant, {
        root: true,
        root_fixed: fixed,
        root_scrollIsAtTop: scrollIsAtTop,
        root_hideAtBreakpointXs: hideAtBreakpoint?.xs,
        root_hideAtBreakpointS: hideAtBreakpoint?.s,
        root_hideAtBreakpointM: hideAtBreakpoint?.m,
      })}
      id="header"
      data-cwv="header"
    >
      <div className={style.sectionWrapper}>
        {withLogo && (
          <Link
            href="/"
            className={cx(style.logoWrapper, {
              smallUntilExtraLarge: withSearch,
            })}
          >
            <Logo />
          </Link>
        )}
        {children}
        {withSearch && (
          <SearchNav
            theme={variant === 'ivory' ? 'white' : 'gray'}
          />
        )}
      </div>

      {withNavigation && (
        <Navigation />
      )}
    </header>
  );
};
export default Header;
